import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import Logo from "../printtest/components/Logo";
import PrintText from "../printtest/components/PrintText";
import PrintController from "../printtest/PrintController";
import {
  TOKEN_CONTAINER_PADDING_PX,
  TOKEN_PAPER_WIDTH_PX,
} from "../../../../utils/tokenPrintPaper";
import { PrintSettingsContext } from "../printtest/PrintSettingsContext";
import { setTokenPrintaction } from "../../../../redux/printstate/printActions";

function TokenDivider({ item }: { item: any }) {
  if (!item?.show) return null;
  const horizontalOffset = Number(item?.left) || 0;
  return (
    <hr
      style={{
        position: "absolute",
        top: item.top,
        left: "50%",
        width: item.size,
        height: 1,
        margin: 0,
        border: "none",
        backgroundColor: "#334155",
        transform: `translate(calc(-50% + ${horizontalOffset}px), 0)`,
      }}
    />
  );
}

function TokenPrint({ viewsetting }: { viewsetting?: boolean }) {
  const { printPosition, clinic, token } = useSelector(
    (state: RootState) => state.tokenPrint
  );

  const issuedAt = useMemo(
    () => new Date(token?.issuedAt ?? new Date()),
    [token?.issuedAt]
  );

  const dateLabel = useMemo(() => {
    const y = issuedAt.getFullYear();
    const m = String(issuedAt.getMonth() + 1).padStart(2, "0");
    const d = String(issuedAt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [issuedAt]);

  const timeLabel = useMemo(
    () =>
      issuedAt
        .toLocaleTimeString("en-GB", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .replace(/\b(a\.?m\.?|p\.?m\.?)\b/gi, (m) =>
          m.startsWith("p") ? "PM" : "AM"
        ),
    [issuedAt]
  );

  const dateTimeLabel = useMemo(
    () => `${dateLabel} · ${timeLabel}`,
    [dateLabel, timeLabel]
  );

  const roomLabel = useMemo(() => {
    const room = String(token?.roomNumber ?? "").trim();
    if (!room || room === "—") return "Room No —";
    return `Room No ${room}`;
  }, [token?.roomNumber]);

  const accentColor = "#1e3a5f";
  const mutedColor = "#475569";
  const contactColor = "#64748b";

  const showContactLine = (value: unknown) =>
    viewsetting || String(value ?? "").trim() !== "";

  return (
    <PrintSettingsContext.Provider
      value={{
        printAction: setTokenPrintaction,
        grabApi: "/clinic/addTokenPrintGrabPosition",
      }}
    >
      <div
        style={{
          background: "white",
          width: TOKEN_PAPER_WIDTH_PX,
          maxWidth: TOKEN_PAPER_WIDTH_PX,
          height: "auto",
        }}
        className="billtext"
      >
        <div
          style={{
            border: printPosition?.container?.show ? "1px solid black" : 0,
            width: printPosition?.container?.left ?? TOKEN_PAPER_WIDTH_PX,
            overflow: "hidden",
            padding: TOKEN_CONTAINER_PADDING_PX,
            position: "relative",
            height: printPosition?.container?.top,
            background: "white",
            boxSizing: "border-box",
          }}
        >
          {printPosition?.logo?.show && (
            <Logo item={printPosition?.logo} src={clinic?.printImages} centered />
          )}

          <PrintText
            item={printPosition?.header}
            title={clinic?.clinicName}
            preserveCase
            centered
            fontWeight={700}
            color={accentColor}
          />

          <PrintText
            item={printPosition?.doctor}
            title={clinic?.drName}
            preserveCase
            centered
            fontWeight={500}
            color={mutedColor}
          />

          <PrintText
            item={printPosition?.tokenNotext}
            title="Token"
            preserveCase
            centered
            fontWeight={400}
            color={mutedColor}
          />

          <PrintText
            item={printPosition?.tokenNo}
            title={token?.tokenNo}
            preserveCase
            centered
            fontWeight={800}
            lineHeight={1}
            color={accentColor}
          />

          <PrintText
            item={printPosition?.roomNumber}
            title={roomLabel}
            preserveCase
            centered
            fontWeight={700}
            color={accentColor}
          />

          <PrintText
            item={printPosition?.patientName}
            title={token?.patientName}
            preserveCase
            centered
            fontWeight={600}
            color="#0f172a"
          />

          <PrintText
            item={printPosition?.datetime}
            title={dateTimeLabel}
            preserveCase
            centered
            fontWeight={400}
            color={mutedColor}
          />

          {showContactLine(clinic?.website) && (
            <PrintText
              item={printPosition?.website}
              title={clinic?.website}
              preserveCase
              centered
              fontWeight={400}
              color={contactColor}
            />
          )}

          {showContactLine(clinic?.email) && (
            <PrintText
              item={printPosition?.email}
              title={clinic?.email}
              preserveCase
              centered
              fontWeight={400}
              color={contactColor}
            />
          )}

          {showContactLine(clinic?.tpNo) && (
            <PrintText
              item={printPosition?.phoneOne}
              title={clinic?.tpNo}
              preserveCase
              centered
              fontWeight={500}
              color={contactColor}
            />
          )}

          {showContactLine(clinic?.phoneTwo) && (
            <PrintText
              item={printPosition?.phoneTwo}
              title={clinic?.phoneTwo}
              preserveCase
              centered
              fontWeight={500}
              color={contactColor}
            />
          )}

          <PrintText
            item={printPosition?.thankYou}
            title={clinic?.thankYouMessage}
            preserveCase
            centered
            fontWeight={600}
            color={mutedColor}
          />

          <TokenDivider item={printPosition?.horizontallineone} />

          <PrintText item={printPosition?.datetimetext} title="Date :" preserveCase centered />
          <PrintText item={printPosition?.timetext} title="Time :" preserveCase centered />
          <PrintText item={printPosition?.time} title={timeLabel} preserveCase centered />
          <PrintText item={printPosition?.doctortext} title="Doctor :" preserveCase centered />
          <PrintText item={printPosition?.roomNumbertext} title="Room :" preserveCase centered />

          <TokenDivider item={printPosition?.horizontallinetwo} />
        </div>
      </div>
      {viewsetting && (
        <PrintController
          item={printPosition}
          api="/clinic/addTokenPrintData"
          action={setTokenPrintaction}
        />
      )}
    </PrintSettingsContext.Provider>
  );
}

export default TokenPrint;
